import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'knx.theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly themeSignal = signal<Theme>(this.getInitialTheme());

  readonly theme = this.themeSignal.asReadonly();
  readonly isDark = computed(() => this.themeSignal() === 'dark');

  constructor() {
    effect(() => {
      const theme = this.themeSignal();
      this.applyThemeClass(theme);
      this.persistTheme(theme);
    });

    this.listenToSystemPreferenceChanges();
  }

  toggle(): void {
    this.setTheme(this.themeSignal() === 'dark' ? 'light' : 'dark');
  }

  setTheme(theme: Theme): void {
    this.themeSignal.set(theme);
  }

  private getInitialTheme(): Theme {
    if (!isPlatformBrowser(this.platformId)) {
      return 'light';
    }

    return this.readStoredTheme() ?? this.getSystemTheme();
  }

  private readStoredTheme(): Theme | null {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);

    if (stored === 'dark' || stored === 'light') {
      return stored;
    }

    return null;
  }

  private getSystemTheme(): Theme {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private applyThemeClass(theme: Theme): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const root = this.document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }

  private persistTheme(theme: Theme): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }

  private listenToSystemPreferenceChanges(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const onSystemPreferenceChange = (event: MediaQueryListEvent): void => {
      if (this.readStoredTheme() !== null) {
        return;
      }

      this.setTheme(event.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', onSystemPreferenceChange);

    this.destroyRef.onDestroy(() => {
      mediaQuery.removeEventListener('change', onSystemPreferenceChange);
    });
  }
}
